/**
 * Tabulator Lifecycle Swimlane Dashboard (Priority 1)
 *
 * Life-critical, production-grade dashboard integrating:
 * - Tabulator grid for workflow instances with grouping, pagination, and inline selection
 * - Search/filter toolbar with date range
 * - CRUD side panel with JSON-safe editing and validated save
 * - Real-time updates via GraphQL subscriptions
 * - Embedded `WorkflowLifecyclePlane` for lifecycle context
 */

import { useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useDynamicFilterConfig } from '../hooks/useDynamicFilterConfig';
import { WorkflowDraftsListView } from './WorkflowDraftsListView';
import { WorkflowSavedListView } from './WorkflowSavedListView';
import { WorkflowHistoryListView } from './WorkflowHistoryListView';
import WorkflowDraftRepository from '../data/repositories/WorkflowDraftRepository';
import {
    Badge,
    Box,
    Button,
    Card,
    Center,
    Collapse,
    Group,
    JsonInput,
    Menu,
    Paper,
    ScrollArea,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Title
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
    IconArrowBackUp,
    IconCheck,
    IconChevronDown,
    IconChevronUp,
    IconDeviceFloppy,
    IconEdit,
    IconHistory,
    IconPlus,
    IconRefresh,
    IconSearch,
    IconTrash,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import RelationshipInspector from './RelationshipInspector';
import RelationshipModal from './RelationshipModal';
import { DrillDownModal } from '../components/DrillDownModal';
import type { FilterClause as AdvancedFilterClause, AdvancedSearchConfig as ExternalAdvancedSearchConfig } from './WorkflowComponentsSearch';
import { WorkflowComponentsSearch } from './WorkflowComponentsSearch';
import WorkflowLifecyclePlane from './WorkflowLifecyclePlane';

// Safe location hook that works with or without Router context
function useSafeLocation() {
    const [location, setLocation] = useState(() => ({
        key: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        state: null
    }));

    useEffect(() => {
        // Listen to browser navigation events for location changes
        const handleLocationChange = () => {
            setLocation({
                key: window.location.href,
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                state: null
            });
        };

        window.addEventListener('popstate', handleLocationChange);

        // Also listen for programmatic navigation (pushState/replaceState)
        const originalPushState = history.pushState;
        const originalReplaceState = history.replaceState;

        history.pushState = function(...args) {
            originalPushState.apply(history, args);
            handleLocationChange();
        };

        history.replaceState = function(...args) {
            originalReplaceState.apply(history, args);
            handleLocationChange();
        };

        return () => {
            window.removeEventListener('popstate', handleLocationChange);
            history.pushState = originalPushState;
            history.replaceState = originalReplaceState;
        };
    }, []);

    return location;
}

type LifecycleRow = {
    id: string;
    key: string;
    name?: string;
    type?: string;
    status?: string;
    progress?: number;
    owner?: string;
    priority?: string;
    createdAt?: number;
    updatedAt?: number;
    deleted?: boolean;
    draft?: Record<string, unknown>;
    // Business categorization fields
    industry?: string;        // NAICS-based or custom industry classification
    department?: string;      // HR, Finance, Operations, Sales, etc.
    businessUnit?: string;    // Division, subsidiary, region
    processType?: string;     // Approval, Review, Transaction, Compliance, etc.
    category?: string;        // Primary business category (Customer Service, Financial, Legal, etc.)
    subcategory?: string;     // More specific categorization
    tags?: string[];          // Flexible tagging system
    criticality?: 'low' | 'medium' | 'high' | 'critical';
    compliance?: string;      // SOX, HIPAA, GDPR, FDA, etc.
    riskLevel?: 'low' | 'medium' | 'high';
    geography?: string;       // Region, country, state for geographical filtering
    customerType?: string;    // B2B, B2C, Internal, Partner
    valueChain?: string;      // Primary, Support, Management (Porter's value chain)
    automationLevel?: 'manual' | 'semi-automated' | 'automated';
    // Regulatory Framework Fields
    regulations?: string[];   // List of regulation codes (HIPAA, SOX, etc.)
    authorityId?: string;     // Authority ID reference
    authorityName?: string;   // Authority name (resolved from federation)
    authorityType?: string;   // REGULATORY, GOVERNMENT, INDUSTRY_BODY, etc.
    complianceLevel?: 'FULLY_COMPLIANT' | 'SUBSTANTIALLY_COMPLIANT' | 'PARTIALLY_COMPLIANT' | 'NON_COMPLIANT' | 'UNDER_REVIEW' | 'EXEMPTED';
    riskClassification?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'CATASTROPHIC';
    jurisdictionalScope?: string[]; // Jurisdictions this workflow operates under
    auditRequirements?: number; // Number of audit requirements
    effectiveDate?: string;   // When regulatory requirements take effect
    expirationDate?: string;  // When requirements expire (if applicable)
    certificationLevel?: 'BASIC' | 'INTERMEDIATE' | 'ADVANCED' | 'EXPERT' | 'MASTER';
    nextAuditDate?: string;   // Next scheduled audit
    regulatoryGaps?: number;  // Number of compliance gaps identified
};

// Function to generate dynamic columns based on backend configuration
const generateDynamicColumns = (filterConfigs: Record<string, string[]>) => [
    // Core/Essential columns (always visible by default)
    { title: 'Name', field: 'name', headerFilter: 'input', width: 200, essential: true, category: 'core' },
    { title: 'Status', field: 'status', headerFilter: 'list', headerFilterParams: { values: filterConfigs.status || ['pending', 'in_progress', 'completed', 'failed', 'stuck'] }, width: 120, essential: true, category: 'core' },
    { title: 'Owner', field: 'owner', headerFilter: 'input', width: 140, essential: true, category: 'core' },
    { title: 'Progress', field: 'progress', formatter: 'progress', width: 100, essential: true, category: 'core' },
    
    // Business context columns (visible by default)
    { title: 'Department', field: 'department', headerFilter: 'list', headerFilterParams: { values: filterConfigs.department || ['HR', 'Finance', 'Operations', 'Sales', 'Legal', 'IT', 'Marketing', 'Procurement'] }, width: 120, category: 'business', defaultVisible: true },
    { title: 'Category', field: 'category', headerFilter: 'list', headerFilterParams: { values: filterConfigs.category || ['Customer Service', 'Financial', 'Legal', 'Compliance', 'Operations', 'Strategic'] }, width: 130, category: 'business', defaultVisible: true },
    { title: 'Priority', field: 'priority', headerFilter: 'list', headerFilterParams: { values: filterConfigs.priority || ['low', 'medium', 'high', 'critical'] }, width: 100, category: 'business', defaultVisible: true },
    
    // Extended business columns (hidden by default)
    { title: 'Process Type', field: 'processType', headerFilter: 'list', headerFilterParams: { values: filterConfigs.processType || ['Approval', 'Review', 'Transaction', 'Compliance', 'Onboarding', 'Maintenance'] }, width: 130, category: 'business', defaultVisible: false },
    { title: 'Criticality', field: 'criticality', headerFilter: 'list', headerFilterParams: { values: filterConfigs.criticality || ['low', 'medium', 'high', 'critical'] }, width: 110, category: 'business', defaultVisible: false },
    { title: 'Industry', field: 'industry', headerFilter: 'input', width: 120, category: 'business', defaultVisible: false },
    { title: 'Business Unit', field: 'businessUnit', headerFilter: 'input', width: 130, category: 'business', defaultVisible: false },
    { title: 'Geography', field: 'geography', headerFilter: 'input', width: 110, category: 'business', defaultVisible: false },
    { title: 'Customer Type', field: 'customerType', headerFilter: 'list', headerFilterParams: { values: filterConfigs.customerType || ['B2B', 'B2C', 'Internal', 'Partner'] }, width: 120, category: 'business', defaultVisible: false },
    
    // Risk & Compliance columns (hidden by default)
    { title: 'Risk Level', field: 'riskLevel', headerFilter: 'list', headerFilterParams: { values: filterConfigs.riskLevel || ['low', 'medium', 'high'] }, width: 100, category: 'compliance', defaultVisible: false },
    { title: 'Compliance', field: 'compliance', headerFilter: 'list', headerFilterParams: { values: filterConfigs.compliance || ['SOX', 'HIPAA', 'GDPR', 'FDA', 'SOC2', 'ISO27001'] }, width: 120, category: 'compliance', defaultVisible: false },
    
    // Regulatory Framework columns (comprehensive regulatory governance)
    { title: 'Authority Name', field: 'authorityName', headerFilter: 'input', width: 150, category: 'regulatory', defaultVisible: false },
    { title: 'Authority Type', field: 'authorityType', headerFilter: 'list', headerFilterParams: { values: filterConfigs.authorityType || ['REGULATORY', 'GOVERNMENT', 'INDUSTRY_BODY', 'PROFESSIONAL_ASSOCIATION', 'STANDARDS_ORGANIZATION'] }, width: 140, category: 'regulatory', defaultVisible: false },
    { title: 'Compliance Level', field: 'complianceLevel', headerFilter: 'list', headerFilterParams: { values: filterConfigs.complianceLevel || ['FULLY_COMPLIANT', 'SUBSTANTIALLY_COMPLIANT', 'PARTIALLY_COMPLIANT', 'NON_COMPLIANT', 'UNDER_REVIEW', 'EXEMPTED'] }, width: 150, category: 'regulatory', defaultVisible: true },
    { title: 'Risk Classification', field: 'riskClassification', headerFilter: 'list', headerFilterParams: { values: filterConfigs.riskClassification || ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'CATASTROPHIC'] }, width: 140, category: 'regulatory', defaultVisible: false },
    { title: 'Regulations', field: 'regulations', formatter: (cell: any) => {
        const regs = cell.getValue();
        return Array.isArray(regs) ? regs.slice(0, 3).join(', ') + (regs.length > 3 ? `... (+${regs.length - 3} more)` : '') : regs || '';
    }, headerFilter: 'input', width: 180, category: 'regulatory', defaultVisible: true },
    { title: 'Jurisdictional Scope', field: 'jurisdictionalScope', formatter: (cell: any) => {
        const scope = cell.getValue();
        return Array.isArray(scope) ? scope.slice(0, 2).join(', ') + (scope.length > 2 ? `... (+${scope.length - 2} more)` : '') : scope || '';
    }, headerFilter: 'input', width: 150, category: 'regulatory', defaultVisible: false },
    { title: 'Audit Requirements', field: 'auditRequirements', width: 120, category: 'regulatory', defaultVisible: false },
    { title: 'Certification Level', field: 'certificationLevel', headerFilter: 'list', headerFilterParams: { values: filterConfigs.certificationLevel || ['BASIC', 'INTERMEDIATE', 'ADVANCED', 'EXPERT', 'MASTER'] }, width: 130, category: 'regulatory', defaultVisible: false },
    { title: 'Effective Date', field: 'effectiveDate', formatter: 'datetime', sorter: 'date', width: 120, category: 'regulatory', defaultVisible: false },
    { title: 'Expiration Date', field: 'expirationDate', formatter: 'datetime', sorter: 'date', width: 130, category: 'regulatory', defaultVisible: false },
    { title: 'Next Audit', field: 'nextAuditDate', formatter: 'datetime', sorter: 'date', width: 120, category: 'regulatory', defaultVisible: false },
    { title: 'Regulatory Gaps', field: 'regulatoryGaps', formatter: (cell: any) => {
        const gaps = cell.getValue();
        return gaps > 0 ? `⚠️ ${gaps}` : gaps === 0 ? '✅ 0' : '';
    }, width: 110, category: 'regulatory', defaultVisible: false },
    
    // Technical columns (hidden by default)
    { title: 'Automation', field: 'automationLevel', headerFilter: 'list', headerFilterParams: { values: ['manual', 'semi-automated', 'automated'] }, width: 120, category: 'technical', defaultVisible: false },
    { title: 'Type', field: 'type', headerFilter: 'input', width: 100, category: 'technical', defaultVisible: false },
    
    // Timestamps (one visible by default)
    { title: 'Updated', field: 'updatedAt', formatter: 'datetime', sorter: 'number', width: 150, category: 'metadata', defaultVisible: true },
    { title: 'Created', field: 'createdAt', formatter: 'datetime', sorter: 'number', width: 150, category: 'metadata', defaultVisible: false },
];

// Get columns with visibility applied
const getVisibleColumns = (allColumns: any[], columnVisibility: Record<string, boolean>) => {
    return allColumns
        .filter(col => {
            // Essential columns are always visible
            if ((col as any).essential) return true;
            // Otherwise use visibility setting or default
            return columnVisibility[col.field] !== undefined
                ? columnVisibility[col.field]
                : (col as any).defaultVisible !== false;
        })
        .map(({ essential, category, defaultVisible, ...col }) => col); // Remove metadata
};

// (statusOrder removed; unused)

function inferField<T = unknown>(obj: any, candidates: string[], fallback?: T): T | undefined {
    for (const path of candidates) {
        const parts = path.split('.');
        let cur: any = obj;
        for (const p of parts) {
            if (cur && typeof cur === 'object' && p in cur) cur = (cur as any)[p];
            else { cur = undefined; break; }
        }
        if (cur !== undefined && cur !== null) return cur as T;
    }
    return fallback;
}

// Enhanced field inference for business categorization
function inferBusinessField(draft: any, field: string, fallback?: any): any {
    const businessFieldMappings: Record<string, string[]> = {
        department: ['department', 'business_unit.department', 'organization.department', 'metadata.department'],
        industry: ['industry', 'naics', 'industry_code', 'business.industry', 'metadata.industry'],
        category: ['category', 'business_category', 'process_category', 'workflow_category', 'type.category'],
        processType: ['process_type', 'workflow_type', 'business_process', 'operation_type', 'procedure_type'],
        criticality: ['criticality', 'priority_level', 'business_criticality', 'importance', 'urgency'],
        riskLevel: ['risk_level', 'risk', 'risk_assessment', 'compliance_risk', 'business_risk'],
        compliance: ['compliance', 'regulatory', 'compliance_framework', 'regulations', 'standards'],
        geography: ['geography', 'region', 'location', 'country', 'market'],
        customerType: ['customer_type', 'client_type', 'audience', 'stakeholder_type'],
        automationLevel: ['automation_level', 'automation', 'manual_level', 'digitization']
    };

    const candidates = businessFieldMappings[field] || [field];
    return inferField(draft, candidates, fallback);
}

function normalizeStatus(v: string | undefined): string | undefined {
    if (!v) return undefined;
    const s = String(v).toLowerCase();
    if (s.includes('complete')) return 'completed';
    if (s.includes('progress') || s === 'running') return 'in_progress';
    if (s.includes('fail') || s.includes('error')) return 'failed';
    if (s.includes('stuck') || s.includes('blocked')) return 'stuck';
    if (s.includes('pending') || s.includes('queued')) return 'pending';
    return s;
}

export const TabulatorLifecycleDashboard: React.FC<{ tenant?: string; titleSearch?: string; titleTable?: string; initialType?: string; tabsMode?: 'table-only' | 'full'; hideSearch?: boolean; externalSearchConfig?: ExternalAdvancedSearchConfig | null }> = ({ tenant, titleSearch = 'Workflow Components Search', titleTable = 'Workflow Components Table', initialType, tabsMode = 'full', hideSearch = false, externalSearchConfig = null }) => {
    const safeTenant = useMemo(() => {
        try { return tenant || (globalThis as any).__TENANT__ || sessionStorage.getItem('tenant') || 'test'; } catch { return tenant || 'test'; }
    }, [tenant]);

    // ✅ REPOSITORY PATTERN: Create repository instance (replaces direct Apollo Client usage)
    const repository = useMemo(() => new WorkflowDraftRepository(), []);

    // Load dynamic filter configurations from backend
    const { filterConfigs, loading: filterConfigLoading } = useDynamicFilterConfig(safeTenant, 'TabulatorLifecycleDashboard');

    // Generate dynamic columns based on backend configuration
    const allLifecycleColumns = useMemo(() => {
        return generateDynamicColumns(filterConfigs);
    }, [filterConfigs]);
    const [loadingComponents, setLoadingComponents] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [rows, setRows] = useState<LifecycleRow[]>([]);
    const [filteredRows, setFilteredRows] = useState<LifecycleRow[]>([]);
    const [selected, setSelected] = useState<LifecycleRow | null>(null);
    const [relOpen, setRelOpen] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editJson, setEditJson] = useState<string>('');
    const [saving, setSaving] = useState(false);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [searchProperty, setSearchProperty] = useState<string>('name');
    const [searchText, setSearchText] = useState<string>('');
    const [advancedClauses, setAdvancedClauses] = useState<AdvancedFilterClause[]>([]);
    const [forceHydrateAll, setForceHydrateAll] = useState<boolean>(false);
    const [liveUpdates, setLiveUpdates] = useState<boolean>(false);
    const [valueSuggestions, setValueSuggestions] = useState<string[]>([]);
    const [quickRangeDays, setQuickRangeDays] = useState<number | null>(null);
    const [lifecycleDashboardOpen, setLifecycleDashboardOpen] = useState<boolean>(true);
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const [viewMode, setViewMode] = useState<'compact' | 'standard' | 'detailed'>('standard');
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [exporting, setExporting] = useState(false);
    const [changeHistory, setChangeHistory] = useState<Array<{ ts: number; diffs: Array<{ path: string; before: any; after: any }>; draft: Record<string, unknown> }>>([]);
    const [unsavedDiffs, setUnsavedDiffs] = useState<Array<{ path: string; before: any; after: any }>>([]);
    const [searchOpen, setSearchOpen] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<string>('table');
    const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
    const fullscreenToolbarRef = useRef<HTMLDivElement>(null);
    const [fullscreenTop, setFullscreenTop] = useState<number>(126);
    const [tableEpoch, setTableEpoch] = useState<number>(0);
    const location = useSafeLocation();
    // Apply external search config when provided (from reusable controller)
    useEffect(() => {
        if (!externalSearchConfig) return;
        try {
            setSelectedTypes(externalSearchConfig.kinds && externalSearchConfig.kinds.length ? externalSearchConfig.kinds : ['Instance']);
            setViewMode(externalSearchConfig.viewMode);
            setDateRange(externalSearchConfig.dateRange);
            setSearchProperty(externalSearchConfig.property || (externalSearchConfig.fullText ? '*' : 'name'));
            setSearchText(externalSearchConfig.fullText || '');
            setAdvancedClauses(externalSearchConfig.clauses || []);
            setForceHydrateAll(!!externalSearchConfig.hydrateAll || (externalSearchConfig.property === '*'));
            setLiveUpdates(!!externalSearchConfig.live);
            setActiveTab('table');
            applyFilters();
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalSearchConfig]);
    // Append-only saved searches (drafts and final saves)
    type SavedSearch = { id: string; name: string; createdAt: number; author?: string; config: { kinds: string[]; viewMode: 'compact' | 'standard' | 'detailed'; dateRange: [number | null, number | null]; fullText?: string; property?: string; clauses?: AdvancedFilterClause[]; hydrateAll?: boolean; quickRangeDays?: number | null; live?: boolean; approvalStatus?: 'pending' | 'approved' | 'rejected' } };
    const [searchDrafts, setSearchDrafts] = useState<SavedSearch[]>([]);
    const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
    const WORKFLOW_COMPONENT_TYPES = useMemo(() => [
        'WorkflowDefinition',
        'Instance',
        'Signal',
        'ExecutionResult',
        'TimelineItem',
        'Task',
        'JobStatus',
        'ActorDescriptor',
        'TransitionCatalog',
        'Policy',
        'Context',
        'Schedule',
        'Resource',
        'Integration',
        'Escalation',
        'OptimizationRecommendation',
        'GovernanceContext'
    ], []);

    // ✅ REPOSITORY PATTERN: Dynamic kinds via repository (was: direct apolloClient.query)
    const [dynamicKinds, setDynamicKinds] = useState<string[] | null>(null);
    useEffect(() => {
        const fetchKinds = async () => {
            try {
                const kinds = await repository.listKinds(safeTenant);
                if (Array.isArray(kinds) && kinds.length) setDynamicKinds(kinds);
            } catch {}
        };
        fetchKinds();
    }, [repository, safeTenant]);
    const ALL_KINDS = dynamicKinds && dynamicKinds.length ? dynamicKinds : WORKFLOW_COMPONENT_TYPES;

    // Dynamic value suggestions based on searchProperty and selectedType via listDictionary(kind)
    // This effect is placed AFTER selectedType declaration below; do not move above
    const [selectedTypes, setSelectedTypes] = useState<string[]>(() => {
        if (initialType && WORKFLOW_COMPONENT_TYPES.includes(initialType)) return [initialType];
        return WORKFLOW_COMPONENT_TYPES;
    });
    const selectedType = useMemo<string>(() => {
        const fromList = selectedTypes && selectedTypes.length ? selectedTypes[0] : undefined;
        const fallback = initialType && WORKFLOW_COMPONENT_TYPES.includes(initialType) ? initialType : 'WorkflowDefinition';
        return String(fromList ?? fallback);
    }, [selectedTypes, initialType]);
    // Now that selectedType is known, load dictionary suggestions when property supports it
    useEffect(() => {
        const loadSuggestions = async () => {
            try {
                if (!searchProperty || !selectedType) { setValueSuggestions([]); return; }
                const DICT_CAPABLE = ['department', 'category', 'processType', 'industry', 'businessUnit', 'geography', 'customerType', 'compliance'];
                const prop = searchProperty.replace(/^draft\./, '');
                if (!DICT_CAPABLE.includes(prop)) { setValueSuggestions([]); return; }
                // ✅ REPOSITORY PATTERN: listDictionary via repository (was: direct apolloClient.query)
                const list = await repository.listDictionary(safeTenant, selectedType);
                setValueSuggestions(Array.isArray(list) ? list.slice(0, 200) : []);
            } catch { setValueSuggestions([]); }
        };
        loadSuggestions();
    }, [repository, safeTenant, searchProperty, selectedType]);

    // React to changes in initialType (when used inside modals)
    useEffect(() => {
        if (!initialType) return;
        if (WORKFLOW_COMPONENT_TYPES.includes(initialType)) {
            setSelectedTypes([initialType]);
        }
    }, [initialType]);

    // Build type-specific columns inferred from draft fields for the selected type
    const typeSpecificColumns = useMemo(() => {
        const cols: any[] = [];
        const add = (title: string, paths: string[], width = 140) => cols.push({ title, field: `__${title.toLowerCase().replace(/\s+/g, '_')}`, formatter: (cell: any) => {
            try {
                const row = cell.getRow().getData() as LifecycleRow;
                const merged: any = { ...row, draft: row?.draft || {} };
                const v = inferField<any>(merged, paths);
                if (v === null || v === undefined) return '';
                if (Array.isArray(v)) return v.slice(0, 3).join(', ');
                if (typeof v === 'object') return JSON.stringify(v);
                return String(v);
            } catch { return ''; }
        }, width, category: 'type', defaultVisible: true });
        switch (selectedType) {
            case 'WorkflowDefinition':
                add('Version', ['draft.version', 'draft.metadata.version']);
                add('States', ['draft.states.length']);
                add('Transitions', ['draft.transitions.length']);
                break;
            case 'Instance':
                add('Status', ['status', 'draft.status', 'draft.state']);
                add('Progress', ['progress', 'draft.progress']);
                add('Owner', ['owner', 'draft.owner', 'draft.metadata.owner']);
                break;
            case 'Task':
                add('Status', ['draft.status']);
                add('Assignee', ['draft.assignee', 'draft.owner']);
                add('Due Date', ['draft.due_date', 'draft.due']);
                break;
            case 'Policy':
                add('Policy Type', ['draft.policy_type', 'draft.type']);
                add('Scope', ['draft.scope']);
                add('Status', ['draft.status']);
                break;
            case 'Context':
                add('Scope', ['draft.scope']);
                add('Variables', ['draft.variables.length']);
                break;
            case 'Schedule':
                add('Cron', ['draft.cron', 'draft.expression']);
                add('Next Run', ['draft.next_run', 'draft.next']);
                add('Status', ['draft.status']);
                break;
            case 'Resource':
                add('Resource Type', ['draft.type', 'draft.resource_type']);
                add('Status', ['draft.status']);
                add('Capacity', ['draft.capacity']);
                break;
            case 'Integration':
                add('Provider', ['draft.provider']);
                add('Status', ['draft.status']);
                add('Retries', ['draft.retries', 'draft.retry_count']);
                break;
            case 'Escalation':
                add('Level', ['draft.level', 'draft.escalation_level']);
                add('Status', ['draft.status']);
                add('Triggered', ['draft.triggered_at', 'draft.last_trigger']);
                break;
            case 'JobStatus':
                add('State', ['draft.state', 'draft.status']);
                add('Started', ['draft.started_at']);
                add('Finished', ['draft.finished_at']);
                break;
            case 'ActorDescriptor':
                add('Role', ['draft.role']);
                add('Name', ['draft.name']);
                add('Email', ['draft.email']);
                break;
            case 'TransitionCatalog':
                add('Transitions', ['draft.transitions.length']);
                add('Guards', ['draft.guards.length']);
                break;
            case 'Signal':
                add('Kind', ['draft.kind', 'draft.type']);
                add('Source', ['draft.source']);
                add('Time', ['draft.timestamp', 'draft.time']);
                break;
            case 'ExecutionResult':
                add('Status', ['draft.status']);
                add('Duration', ['draft.duration_ms']);
                add('Finished', ['draft.finished_at']);
                break;
            case 'TimelineItem':
                add('ExecutionResult', ['draft.executionresult', 'draft.type']);
                add('At', ['draft.at', 'draft.timestamp']);
                add('ActorDescriptor', ['draft.actordescriptor', 'draft.user']);
                break;
            case 'OptimizationRecommendation':
                add('Severity', ['draft.severity']);
                add('Category', ['draft.category']);
                add('Status', ['draft.status']);
                break;
            default:
                break;
        }
        return cols;
    }, [selectedType]);

    const getVisibleColumnsMerged = useCallback((columnVisibility: Record<string, boolean>) => {
        const base = [...allLifecycleColumns, ...typeSpecificColumns];
        return base
            .filter(col => {
                if ((col as any).essential) return true;
                return columnVisibility[col.field] !== undefined
                    ? columnVisibility[col.field]
                    : (col as any).defaultVisible !== false;
            })
            .map(({ essential, category, defaultVisible, ...col }) => col);
    }, [allLifecycleColumns, typeSpecificColumns]);
    const allDraftsHydratedRef = useRef<boolean>(false);
    const [hydratingDrafts, setHydratingDrafts] = useState<boolean>(false);
    const reflowTable = useCallback(() => {
        try { table.current?.setColumns?.(getVisibleColumnsMerged(columnVisibility) as any); } catch {}
        try { table.current?.redraw?.(true); } catch {}
    }, [columnVisibility, getVisibleColumnsMerged]);

    // Ensure columns are reapplied when navigating back to the route or when tab becomes visible
    useEffect(() => {
        const force = () => {
            try { table.current?.setColumns?.(getVisibleColumnsMerged(columnVisibility) as any); } catch {}
            try { table.current?.redraw?.(true); } catch {}
        };
        const onPageShow = () => setTimeout(force, 0);
        const onPopState = () => setTimeout(force, 0);
        const onVisibility = () => { if (document.visibilityState === 'visible') setTimeout(force, 0); };
        window.addEventListener('pageshow', onPageShow);
        window.addEventListener('popstate', onPopState);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pageshow', onPageShow);
            window.removeEventListener('popstate', onPopState);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [columnVisibility, viewMode, getVisibleColumnsMerged]);

    // Resolve initial tab from URL (?tab=table|overview|drafts|saved) or trailing path
    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            const qp = (url.searchParams.get('tab') || '').toLowerCase();
            const validTabs = ['overview', 'table', 'drafts', 'saved', 'history'];

            // If valid tab in URL, use it (unless table-only mode excludes it)
            if (validTabs.includes(qp)) {
                // In table-only mode, overview is not available
                if (tabsMode === 'table-only' && qp === 'overview') {
                    setActiveTab('table');
                    return;
                }
                setActiveTab(qp);
                return;
            }

            // Fallback: last path segment
            const parts = url.pathname.split('/').filter(Boolean);
            const last = parts[parts.length - 1]?.toLowerCase() ?? '';
            if (last && validTabs.includes(last)) {
                if (tabsMode === 'table-only' && last === 'overview') {
                    setActiveTab('table');
                    return;
                }
                setActiveTab(last);
                return;
            }

            // Final fallback: default to table
            setActiveTab('table');
        } catch {
            setActiveTab('table');
        }
    }, [tabsMode]);

    // Keep URL in sync when tab changes (query param 'tab')
    useEffect(() => {
        try {
            const url = new URL(window.location.href);
            const current = (url.searchParams.get('tab') || '').toLowerCase();
            if (current !== activeTab) {
                url.searchParams.set('tab', activeTab);
                window.history.replaceState({}, '', url.toString());
            }
        } catch {}
    }, [activeTab]);

    // Bump epoch on route changes so table fully remounts/rebuilds when navigating away/back
    useEffect(() => {
        try { setTableEpoch((e) => e + 1); } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    const baseDraftRef = useRef<Record<string, unknown> | null>(null);
    // Load saved search drafts/saves
    useEffect(() => {
        try {
            const d = JSON.parse(localStorage.getItem('workflow:searchDrafts') || '[]');
            const s = JSON.parse(localStorage.getItem('workflow:savedSearches') || '[]');
            setSearchDrafts(Array.isArray(d) ? d : []);
            setSavedSearches(Array.isArray(s) ? s : []);
        } catch {}
    }, []);

    const persistSearches = (drafts: SavedSearch[], saves: SavedSearch[]) => {
        try { localStorage.setItem('workflow:searchDrafts', JSON.stringify(drafts)); } catch {}
        try { localStorage.setItem('workflow:savedSearches', JSON.stringify(saves)); } catch {}
    };

    const buildCurrentSearchConfig = (): SavedSearch['config'] => ({
        kinds: selectedTypes && selectedTypes.length ? selectedTypes : ['Instance'],
        viewMode,
        dateRange: [dateRange[0] ? dateRange[0].getTime() : null, dateRange[1] ? dateRange[1].getTime() : null],
        fullText: searchText || '',
        property: searchProperty || 'name',
        clauses: advancedClauses || [],
        hydrateAll: forceHydrateAll,
        quickRangeDays: null,
        live: liveUpdates,
        approvalStatus: 'pending'
    });

    const handleSaveSearchDraft = () => {
        const id = `draft-${Date.now()}`;
        const cfg = buildCurrentSearchConfig();
        const author = (globalThis as any).__AUTH_USER__ || undefined;
        const name = `Draft ${new Date().toLocaleString()}`;
        const next = [{ id, name, createdAt: Date.now(), author, config: cfg }, ...searchDrafts].slice(0, 50);
        setSearchDrafts(next);
        persistSearches(next, savedSearches);
        setActiveTab('drafts');
        notifications.show({ title: 'Draft Saved', message: name, color: 'blue' });
    };

    const handleSaveSearchFinal = () => {
        const id = `save-${Date.now()}`;
        const cfg = buildCurrentSearchConfig();
        cfg.approvalStatus = 'pending';
        const author = (globalThis as any).__AUTH_USER__ || undefined;
        const name = `Saved Search ${new Date().toLocaleString()}`;
        const next = [{ id, name, createdAt: Date.now(), author, config: cfg }, ...savedSearches].slice(0, 50);
        setSavedSearches(next);
        persistSearches(searchDrafts, next);
        setActiveTab('saved');
        notifications.show({ title: 'Search Saved', message: name, color: 'green' });
    };

    const applySavedSearch = (s: SavedSearch) => {
        try {
            setSelectedTypes(s.config.kinds);
            setViewMode(s.config.viewMode);
            setDateRange([
                s.config.dateRange[0] ? new Date(s.config.dateRange[0]) : null,
                s.config.dateRange[1] ? new Date(s.config.dateRange[1]) : null
            ]);
            setSearchProperty(s.config.property || 'name');
            setSearchText(s.config.fullText || '');
            setAdvancedClauses(s.config.clauses || []);
            setForceHydrateAll(!!s.config.hydrateAll);
            setLiveUpdates(!!s.config.live);
            setActiveTab('table');
            applyFilters();
        } catch {}
    };

    // ✅ REPOSITORY PATTERN: Workflow history state (was: useQuery<Record<string, any>>(WORKFLOW_HISTORY))
    const [historyData, setHistoryData] = useState<{ workflowHistory: any[] } | null>(null);
    useEffect(() => {
        if (!selected?.key) {
            setHistoryData(null);
            return;
        }
        const fetchHistory = async () => {
            try {
                const history = await repository.getWorkflowHistory(safeTenant, selected.key, 50);
                setHistoryData({ workflowHistory: history });
            } catch {
                setHistoryData(null);
            }
        };
        fetchHistory();
    }, [repository, safeTenant, selected?.key]);

    // ✅ REPOSITORY PATTERN: Selected draft data state (was: useQuery<Record<string, any>>(GET_DRAFT))
    const [selectedDraftData, setSelectedDraftData] = useState<{ getDraft: any } | null>(null);
    useEffect(() => {
        if (!selected?.key) {
            setSelectedDraftData(null);
            return;
        }
        const fetchDraft = async () => {
            try {
                const draft = await repository.getDraft(
                    safeTenant,
                    (selected?.type as string) || 'Instance',
                    selected.key
                );
                setSelectedDraftData({ getDraft: draft });
            } catch {
                setSelectedDraftData(null);
            }
        };
        fetchDraft();
    }, [repository, safeTenant, selected?.key, selected?.type]);

    const tableRef = useRef<HTMLDivElement>(null);
    const table = useRef<any | null>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    // Track sidebar collapsed state to adjust layout
    const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
    // Reflow table on layout changes
    useEffect(() => {
        const handle = () => {
            reflowTable();
        };
        window.addEventListener('resize', handle);
        window.addEventListener('layout:sidebarToggled' as any, ((e: any) => { try { setSidebarCollapsed(!!e?.detail?.collapsed); } catch {} handle(); }) as any);
        window.addEventListener('layout:contentToggled' as any, handle as any);
        // Observe container size
        let ro: ResizeObserver | null = null;
        try {
            if (tableContainerRef.current && 'ResizeObserver' in window) {
                ro = new ResizeObserver(() => handle());
                ro.observe(tableContainerRef.current);
            }
        } catch {}
        return () => {
            window.removeEventListener('resize', handle);
            window.removeEventListener('layout:sidebarToggled' as any, handle as any);
            window.removeEventListener('layout:contentToggled' as any, handle as any);
            try { ro && ro.disconnect(); } catch {}
        };
    }, [reflowTable]);

    // Reflow when switching to table tab
    useEffect(() => {
        if (activeTab === 'table') {
            setTimeout(reflowTable, 0);
        }
    }, [activeTab, reflowTable]);

    // Reflow when entering/exiting fullscreen
    useEffect(() => {
        const forceReflow = () => { reflowTable(); try { table.current?.setHeight?.('100%'); } catch {}; try { window.dispatchEvent(new Event('resize')); } catch {} };
        // immediate and after layout settles
        setTimeout(forceReflow, 50);
        setTimeout(forceReflow, 250);
        const computeTop = () => {
            try {
                const appHeader = 70;
                const tb = fullscreenToolbarRef.current?.offsetHeight || 64;
                setFullscreenTop(appHeader + tb);
            } catch {}
        };
        if (isFullScreen) {
            setTimeout(computeTop, 0);
            window.addEventListener('resize', computeTop);
        }
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                try { window.dispatchEvent(new Event('layout:expandSidebar' as any)); } catch {}
                setIsFullScreen(false);
            }
        };
        if (isFullScreen) {
            window.addEventListener('keydown', onEsc);
        }
        return () => {
            window.removeEventListener('keydown', onEsc);
            window.removeEventListener('resize', () => {});
        };
    }, [isFullScreen, columnVisibility, reflowTable]);

    // Reflow on view/visibility changes in fullscreen
    useEffect(() => { if (isFullScreen) setTimeout(reflowTable, 0); }, [viewMode, columnVisibility, isFullScreen, reflowTable]);

    // Get current visible columns based on view mode and visibility settings
    const visibleColumns = useMemo(() => {
        let columns = getVisibleColumnsMerged(columnVisibility);
        
        // Apply view mode filters
        if (viewMode === 'compact') {
            // Only show essential columns in compact mode
            columns = columns.filter(col => 
                ['name', 'status', 'owner', 'priority', 'updatedAt'].includes(col.field)
            );
        } else if (viewMode === 'detailed') {
            // Show more columns in detailed mode, override some visibility settings
            const detailedColumns = getVisibleColumns(allLifecycleColumns, {
                ...columnVisibility,
                processType: true,
                industry: true,
                businessUnit: true,
                riskLevel: true,
                compliance: true,
                automationLevel: true,
                createdAt: true
            });
            columns = detailedColumns;
        }
        
        return columns;
    }, [columnVisibility, viewMode, getVisibleColumnsMerged]);

    // Dynamic loader for all selected component types
    const loadData = useCallback(async () => {
        setLoadingComponents(true);
        setErrorMessage(null);
        try {
            // ✅ REPOSITORY PATTERN: listDrafts + getDraft via repository (was: apolloClient.query)
            const kinds = (selectedTypes.length ? selectedTypes : [selectedType]);
            const results = await Promise.all(
                kinds.map(async (kind) => {
                    try {
                        const list = await repository.listDrafts(safeTenant, kind);

                        // Load draft data immediately for each item
                        const rowsWithDrafts = await Promise.all(list.map(async (item: any): Promise<LifecycleRow> => {
                            try {
                                const draftData = await repository.getDraft(safeTenant, kind, item.key);
                                const draft = draftData?.draft || {};

                                return {
                                    id: `${kind}:${String(item.key)}`,
                                    key: String(item.key),
                                    name: draft.name || String(item.key),
                                    type: draft.type || kind,
                                    status: draft.status,
                                    progress: draft.progress,
                                    owner: draft.owner || item.owner || 'System',
                                    priority: draft.priority,
                                    createdAt: Number((item as any).updatedMs || Date.now()),
                                    updatedAt: Number((item as any).updatedMs || 0),
                                    deleted: !!item.deleted,
                                    draft: draft,
                                    // Use draft data for all metadata fields
                                    department: draft.department,
                                    category: draft.category,
                                    processType: draft.processType,
                                    industry: draft.industry,
                                    businessUnit: draft.businessUnit,
                                    geography: draft.geography,
                                    customerType: draft.customerType,
                                    criticality: draft.criticality,
                                    riskLevel: draft.riskLevel,
                                    automationLevel: draft.automationLevel,
                                    compliance: draft.compliance,
                                    regulations: draft.regulations,
                                    authorityName: draft.authorityName,
                                    authorityType: draft.authorityType,
                                    complianceLevel: draft.complianceLevel,
                                    riskClassification: draft.riskClassification,
                                    jurisdictionalScope: draft.jurisdictionalScope,
                                    auditRequirements: draft.auditRequirements,
                                    certificationLevel: draft.certificationLevel,
                                    effectiveDate: draft.effectiveDate,
                                    nextAuditDate: draft.nextAuditDate,
                                    regulatoryGaps: draft.regulatoryGaps,
                                    description: draft.description,
                                } as LifecycleRow;
                            } catch (err) {
                                // Fallback to minimal row if draft loading fails
                                return {
                                    id: `${kind}:${String(item.key)}`,
                                    key: String(item.key),
                                    name: String(item.key),
                                    type: kind,
                                    owner: item.owner || 'System',
                                    createdAt: Number((item as any).updatedMs || Date.now()),
                                    updatedAt: Number((item as any).updatedMs || 0),
                                    deleted: !!item.deleted,
                                    draft: {},
                                } as LifecycleRow;
                            }
                        }));

                        return rowsWithDrafts;
                    } catch {
                        return [] as LifecycleRow[];
                    }
                })
            );
            const merged = results.flat();
            merged.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
            setRows(merged);
            setFilteredRows(merged);
        } catch (e: any) {
            setRows([]);
            setFilteredRows([]);
            setErrorMessage(e?.message || 'Failed to load components');
        } finally {
            setLoadingComponents(false);
        }
    }, [repository, safeTenant, selectedType, selectedTypes]);

    // FIXED: Remove loadData dependency to prevent infinite loop
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Optional live updates: simple polling when enabled - FIXED: Stable dependencies
    useEffect(() => {
        if (!liveUpdates) return undefined;
        const id = window.setInterval(() => { try { loadData(); } catch {} }, 5000);
        return () => { window.clearInterval(id); };
    }, [liveUpdates, loadData]);

    // Real-time updates via GraphQL subscription (tenant activity stream)
    const TENANT_ACTIVITY_STREAM_SUB = gql`
      subscription TenantActivityStream($tenant: String!, $eventTypes: [String!]) {
        tenantActivityStream(tenant: $tenant, eventTypes: $eventTypes) {
          eventId
          eventType
          entityType
          entityId
          timestamp
        }
      }
    `;
    // Map selected kind to eventTypes filter for lower-noise subscription
    const mapKindToEventTypes = (kind: string): string[] | undefined => {
        const k = kind.toLowerCase();
        if (['instance', 'workflowdefinition', 'transitioncatalog', 'executionresult', 'timelineitem'].some(x => k.includes(x))) return ['WORKFLOW'];
        if (k.includes('task')) return ['TASK'];
        if (k.includes('policy')) return ['POLICY'];
        if (k.includes('schedule')) return ['SCHEDULE'];
        if (k.includes('resource')) return ['RESOURCE'];
        if (k.includes('integration')) return ['INTEGRATION'];
        if (k.includes('escalation')) return ['ESCALATION'];
        return undefined;
    };
    const refreshDebounceRef = useRef<number | null>(null);
    const sub = useSubscription<Record<string, any>>(TENANT_ACTIVITY_STREAM_SUB, {
        variables: { tenant: safeTenant, eventTypes: mapKindToEventTypes(selectedType) },
        skip: !liveUpdates,
        onData: () => {
            try {
                if (refreshDebounceRef.current) window.clearTimeout(refreshDebounceRef.current);
                refreshDebounceRef.current = window.setTimeout(() => { try { loadData(); } catch {} }, 500);
            } catch {}
        },
        onError: () => { /* ignore subscription errors; polling still active */ }
    } as any);

    // Update selected item with full draft data when loaded
    useEffect(() => {
        if (selected && selectedDraftData?.getDraft) {
            const draft = selectedDraftData.getDraft.draft as Record<string, unknown>;
            setEditJson(JSON.stringify(draft, null, 2));
            baseDraftRef.current = draft;
            // Load history from localStorage
            try {
                const key = `workflowHistory:${safeTenant}:${selected.key}`;
                const raw = localStorage.getItem(key);
                setChangeHistory(raw ? JSON.parse(raw) : []);
            } catch { setChangeHistory([]); }
            // Update selected row with hydrated data including business fields
            setSelected(prev => prev ? {
                ...prev,
                draft,
                name: inferField<string>(draft, ['name', 'title', 'metadata.name', 'data.name'], prev.name),
                status: normalizeStatus(inferField<string>(draft, ['status', 'state', 'current_state', 'execution.status'], prev.status)),
                progress: inferField<number>(draft, ['progress', 'metrics.progress', 'execution.progress'], prev.progress),
                priority: inferField<string>(draft, ['priority', 'metadata.priority'], prev.priority),
                // Enhanced business categorization from draft data
                department: inferBusinessField(draft, 'department', prev.department),
                industry: inferBusinessField(draft, 'industry', prev.industry),
                category: inferBusinessField(draft, 'category', prev.category),
                processType: inferBusinessField(draft, 'processType', prev.processType),
                businessUnit: inferField<string>(draft, ['business_unit', 'unit', 'division', 'subsidiary'], prev.businessUnit),
                geography: inferBusinessField(draft, 'geography', prev.geography),
                customerType: inferBusinessField(draft, 'customerType', prev.customerType),
                criticality: inferBusinessField(draft, 'criticality', prev.criticality),
                riskLevel: inferBusinessField(draft, 'riskLevel', prev.riskLevel),
                compliance: inferBusinessField(draft, 'compliance', prev.compliance),
                automationLevel: inferBusinessField(draft, 'automationLevel', prev.automationLevel),
                tags: inferField<string[]>(draft, ['tags', 'labels', 'keywords'], prev.tags),
                subcategory: inferField<string>(draft, ['subcategory', 'sub_category', 'detailed_category'], prev.subcategory),
                valueChain: inferField<string>(draft, ['value_chain', 'business_process_type', 'process_category'], prev.valueChain)
            } : null);
        }
    }, [selected, selectedDraftData, safeTenant]);

    // Prefer server history if available
    useEffect(() => {
        if (historyData?.workflowHistory && Array.isArray(historyData.workflowHistory)) {
            try { setChangeHistory(historyData.workflowHistory as any); } catch {}
        }
    }, [historyData?.workflowHistory]);

    // Initialize Tabulator with robust mounting checks to avoid race conditions
    useEffect(() => {
        if (table.current) {
            try {
                table.current.destroy();
            } catch {}
            table.current = null;
        }
        
        // Determine initial columns; prefer current visibleColumns; otherwise compute merged defaults
        const initColumns = (visibleColumns && visibleColumns.length)
            ? (visibleColumns as any)
            : (getVisibleColumnsMerged(columnVisibility) as any);

        let cancelled = false;
        let retryCount = 0;
        const maxRetries = 10;
        
        const mount = () => {
            if (cancelled || retryCount >= maxRetries) return;
            
            const el = tableRef.current;
            if (!el) {
                retryCount++;
                setTimeout(mount, 100);
                return;
            }
            
            // Ensure element is in DOM and has dimensions
            if (!document.body.contains(el)) {
                retryCount++;
                setTimeout(mount, 100);
                return;
            }
            
            // Wait for element to have dimensions
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) {
                retryCount++;
                setTimeout(mount, 100);
                return;
            }
            
            try {
                // Use a simpler configuration and provide data directly
                table.current = new TabulatorFull(el, {
                    data: filteredRows,  // Provide data directly
                    columns: initColumns,
                    layout: 'fitColumns',
                    pagination: false,
                    placeholder: 'No workflows found…',
                    height: 400,
                    selectable: 'highlight', // Allow multiple row selection
                    selectableCheck: () => true, // All rows selectable
                    rowClick: (_e: any, row: any) => {
                        const r = row.getData() as LifecycleRow;
                        setSelected(r);
                        setIsEditing(false);
                        setEditJson('{}');
                        setRelOpen(true);
                    },
                    rowSelectionChanged: (data: any[], rows: any[]) => {
                        const selectedIds = data.map((row) => row.id);
                        setSelectedRows(selectedIds);
                    }
                });
                // Extra delayed reflows to handle layout after navigation
                setTimeout(() => { try { table.current?.setColumns?.(visibleColumns as any); table.current?.redraw?.(true); } catch {} }, 50);
                setTimeout(() => { try { table.current?.redraw?.(true); } catch {} }, 250);
                setTimeout(() => { try { table.current?.redraw?.(true); } catch {} }, 500);
            } catch (error) {
                retryCount++;
                if (retryCount < maxRetries) {
                    setTimeout(mount, 200);
                }
            }
        };
        
        // Start mounting with a small delay to ensure DOM is ready
        setTimeout(mount, 100);
        
        return () => { 
            cancelled = true;
            if (table.current) {
                try {
                    table.current.destroy();
                } catch {}
                table.current = null;
            }
        };
    }, [filteredRows, visibleColumns, viewMode, tableEpoch, selectedType, getVisibleColumnsMerged]);

    // Update Table data when filteredRows change (but table exists and is initialized)
    useEffect(() => {
        if (!table.current || !filteredRows.length) return;
        
        try { table.current.replaceData(filteredRows); } catch {}
    }, [filteredRows]);

    // Reactively update Tabulator columns when visibility or view mode changes
    useEffect(() => {
        if (!table.current) return;
        try {
            (table.current as any).setColumns(visibleColumns as any);
        } catch {
            // ignore if table not ready
        }
    }, [visibleColumns, selectedType]);

    // Reflow when component type changes to ensure Tabulator updates columns immediately
    useEffect(() => {
        reflowTable();
    }, [selectedType, typeSpecificColumns, getVisibleColumnsMerged, reflowTable]);

    const hydrateAllDraftsIfNeeded = useCallback(async () => {
        if (allDraftsHydratedRef.current) return;
        setHydratingDrafts(true);
        try {
            const BATCH = 6;
            const toHydrate = rows.filter(r => !r.draft || Object.keys(r.draft || {}).length === 0);
            for (let i = 0; i < toHydrate.length; i += BATCH) {
                const slice = toHydrate.slice(i, i + BATCH);
                // ✅ REPOSITORY PATTERN: getDraft via repository (was: apolloClient.query)
                const fetched = await Promise.all(slice.map(async (r) => {
                    try {
                        const draftData = await repository.getDraft(
                            safeTenant,
                            (r.type as string) || 'Instance',
                            r.key
                        );
                        const draft = draftData?.draft || {};
                        return { id: r.id, draft } as { id: string; draft: Record<string, unknown> };
                    } catch {
                        return { id: r.id, draft: {} as Record<string, unknown> };
                    }
                }));
                if (Array.isArray(fetched) && fetched.length) {
                    setRows(prev => prev.map((item: LifecycleRow) => {
                        const match = fetched.find((f) => f && f.id === item.id);
                        if (!match) return item;
                        return { ...item, draft: match.draft } as LifecycleRow;
                    }));
                }
            }
            allDraftsHydratedRef.current = true;
        } finally {
            setHydratingDrafts(false);
        }
    }, [rows, repository, safeTenant]);

    const applyFilters = useCallback(async () => {
        const [from, to] = dateRange;
        // Hydrate all drafts when required for full-text or advanced clauses
        if (searchProperty === '*' || forceHydrateAll || (advancedClauses && advancedClauses.length > 0)) {
            await hydrateAllDraftsIfNeeded();
        }

        const matchOperator = (value: unknown, op: string, needle: string): boolean => {
            const s = (value === undefined || value === null) ? '' : String(value);
            switch (op) {
                case 'equals': return s === needle;
                case 'startsWith': return s.startsWith(needle);
                case 'endsWith': return s.endsWith(needle);
                case 'regex':
                    try { return new RegExp(needle, 'i').test(s); } catch { return false; }
                case 'contains':
                default: return s.toLowerCase().includes(needle.toLowerCase());
            }
        };

        const f = rows.filter(r => {
            if (from && r.updatedAt && r.updatedAt < from.getTime()) return false;
            if (to && r.updatedAt && r.updatedAt > to.getTime()) return false;

            // Full-text or property search
            if (searchText && searchProperty) {
                if (searchProperty === '*') {
                    try {
                        const blob = JSON.stringify({ ...r, draft: r.draft }).toLowerCase();
                        if (!blob.includes(searchText.toLowerCase())) return false;
                    } catch { return false; }
                } else {
                    // Try row field, then draft field
                    const merged: any = { ...r, draft: r?.draft || {} };
                    const val = inferField<string>(merged, [searchProperty, `draft.${searchProperty}`], '');
                    if (!matchOperator(val, 'contains', searchText)) return false;
                }
            }

            // Advanced clauses (AND semantics)
            if (advancedClauses && advancedClauses.length) {
                const merged: any = { ...r, draft: r?.draft || {} };
                for (const clause of advancedClauses) {
                    const val = inferField<string>(merged, [clause.field, `draft.${clause.field}`], '');
                    if (!matchOperator(val, clause.operator, clause.value || '')) {
                        return false;
                    }
                }
            }

            return true;
        });
        setFilteredRows(f);
    }, [rows, dateRange, searchText, searchProperty, advancedClauses, forceHydrateAll, hydrateAllDraftsIfNeeded]);

    const clearFilters = useCallback(() => {
        setDateRange([null, null]);
        setSearchProperty('name');
        setSearchText('');
        setFilteredRows(rows);
    }, [rows]);

    const handleSave = useCallback(async () => {
        if (!selected) return;
        setSaving(true);
        try {
            let parsed: Record<string, unknown> = {};
            try { parsed = editJson && editJson.trim().length ? JSON.parse(editJson) : {}; } catch (e: any) { throw new Error('Invalid JSON'); }
            // ✅ REPOSITORY PATTERN: updateDraft via repository (was: updateDraftMutation)
            await repository.updateDraft(
                safeTenant,
                (selected.type as string) || 'Instance',
                selected.key,
                parsed
            );
            await loadData();
            setIsEditing(false);
        } catch {
            // keep editor open
        } finally {
            setSaving(false);
        }
    }, [selected, editJson, repository, safeTenant, loadData]);

    const handleDelete = useCallback(async () => {
        if (!selected) return;
        try {
            // ✅ REPOSITORY PATTERN: deleteDraft via repository (was: deleteDraftMutation)
            await repository.deleteDraft(
                safeTenant,
                (selected.type as string) || 'Instance',
                selected.key
            );
            setSelected(null);
            await loadData();
        } catch {
            // ignore
        }
    }, [selected, repository, safeTenant, loadData]);

    const searchProperties = useMemo(() => ([
        { value: '*', label: 'Any Property' },
        { value: 'name', label: 'Workflow Name' },
        { value: 'department', label: 'Department' },
        { value: 'category', label: 'Category' },
        { value: 'processType', label: 'Process Type' },
        { value: 'industry', label: 'Industry' },
        { value: 'businessUnit', label: 'Business Unit' },
        { value: 'geography', label: 'Geography' },
        { value: 'compliance', label: 'Compliance' },
        { value: 'owner', label: 'Owner' },
        { value: 'status', label: 'Status' },
        { value: 'type', label: 'Workflow Type' },
    ]), []);

    // Column visibility controls
    const toggleColumnVisibility = useCallback((fieldName: string) => {
        setColumnVisibility(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    }, []);

    const resetToDefaults = useCallback(() => {
        setColumnVisibility({});
        setViewMode('standard');
    }, []);

    const columnsByCategory = useMemo(() => {
        const categories: Record<string, typeof allLifecycleColumns> = {
            core: [],
            business: [],
            compliance: [],
            regulatory: [], // New regulatory framework category
            technical: [],
            metadata: []
        };
        
        allLifecycleColumns.forEach(col => {
            const category = (col as any)?.category || 'core';
            if (categories[category]) {
                categories[category].push(col);
            }
        });
        
        return categories;
    }, [allLifecycleColumns]);

    // Row management functions
    const addNewRow = useCallback(() => {
        // Use dynamic filter configurations or fallback to defaults
        const departments = filterConfigs.department || ['HR', 'Finance', 'Operations', 'Sales', 'Legal', 'IT', 'Marketing', 'Procurement'];
        const categories = filterConfigs.category || ['Customer Service', 'Financial', 'Legal', 'Compliance', 'Operations', 'Strategic'];
        const processTypes = filterConfigs.processType || ['Approval', 'Review', 'Transaction', 'Compliance', 'Onboarding', 'Maintenance'];
        const industries = ['Healthcare', 'Financial Services', 'Technology', 'Manufacturing', 'Retail', 'Real Estate'];
        const businessUnits = ['North America', 'EMEA', 'APAC', 'Corporate', 'Commercial', 'Consumer'];
        const geographies = ['US-East', 'US-West', 'EU', 'Asia', 'Global'];
        const customerTypes = filterConfigs.customerType || ['B2B', 'B2C', 'Internal', 'Partner'];
        const priorities = filterConfigs.priority || ['low', 'medium', 'high', 'critical'];
        const riskLevels = filterConfigs.riskLevel || ['low', 'medium', 'high'];
        const automationLevels = ['manual', 'semi-automated', 'automated'];
        const complianceTypes = filterConfigs.compliance || ['SOX', 'HIPAA', 'GDPR', 'FDA', 'SOC2', 'ISO27001'];

        const newId = `new-${Date.now()}`;
        const randomIndex = Math.floor(Math.random() * departments.length);
        
        const newRow: LifecycleRow = {
            id: newId,
            key: `new-workflow-${newId}`,
            name: 'New Workflow',
            type: 'Instance',
            status: 'pending',
            progress: 0,
            owner: 'Unassigned',
            priority: 'medium',
            createdAt: Date.now(),
            updatedAt: Date.now(),
            deleted: false,
            department: departments[randomIndex],
            category: categories[randomIndex % categories.length],
            processType: processTypes[randomIndex % processTypes.length],
            industry: industries[randomIndex % industries.length],
            businessUnit: businessUnits[randomIndex % businessUnits.length],
            geography: geographies[randomIndex % geographies.length],
            customerType: customerTypes[randomIndex % customerTypes.length],
            criticality: priorities[randomIndex % priorities.length] as 'low' | 'medium' | 'high' | 'critical',
            riskLevel: riskLevels[randomIndex % riskLevels.length] as 'low' | 'medium' | 'high',
            automationLevel: automationLevels[randomIndex % automationLevels.length] as 'manual' | 'semi-automated' | 'automated',
            compliance: complianceTypes[randomIndex % complianceTypes.length],
            draft: {}
        };

        const updatedRows = [...rows, newRow];
        setRows(updatedRows);
        setFilteredRows(updatedRows);
        try { table.current?.addData([newRow], true); } catch {}
        notifications.show({ title: 'Row Added', message: `${newRow.name}`, color: 'green', icon: <IconCheck size={16} /> });
    }, [rows, filterConfigs]);

    const deleteSelectedRows = useCallback(() => {
        if (selectedRows.length === 0) return;
        modals.openConfirmModal({
            title: 'Delete selected rows?',
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red', leftSection: <IconTrash size={14} /> },
            onConfirm: () => {
                const updatedRows = rows.filter(row => !selectedRows.includes(row.id));
                setRows(updatedRows);
                setFilteredRows(updatedRows);
                try {
                    if (table.current) {
                        selectedRows.forEach(id => { try { table.current?.getRow(id)?.delete(); } catch {} });
                    }
                } catch {}
                setSelectedRows([]);
                if (selected && selectedRows.includes(selected.id)) setSelected(null);
                notifications.show({ title: 'Deleted', message: `${selectedRows.length} row(s) removed`, color: 'red', icon: <IconX size={16} /> });
            }
        });
    }, [rows, selectedRows, selected]);

    const duplicateSelectedRow = useCallback(() => {
        if (selectedRows.length !== 1) return;
        
        const originalRow = rows.find(row => row.id === selectedRows[0]);
        if (!originalRow) return;
        
        const newId = `duplicate-${Date.now()}`;
        const duplicatedRow: LifecycleRow = {
            ...originalRow,
            id: newId,
            key: `${originalRow.key}-copy`,
            name: `${originalRow.name} (Copy)`,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };
        
        const updatedRows = [...rows, duplicatedRow];
        setRows(updatedRows);
        setFilteredRows(updatedRows);
        
        try { table.current?.addData([duplicatedRow], true); } catch {}
        notifications.show({ title: 'Duplicated', message: `${duplicatedRow.name}`, color: 'blue' });
    }, [rows, selectedRows]);

    // Export current view to CSV/JSON
    const exportRows = useCallback(async (format: 'csv' | 'json') => {
        try {
            setExporting(true);
            const fileName = `workflows_${Date.now()}.${format}`;
            if (format === 'csv') {
                const csv = (table.current as any)?.getData?.()?.map((r: any) => r) || filteredRows;
                const headers = visibleColumns.map(c => c.field);
                const lines = [headers.join(',')].concat(csv.map((r: any) => headers.map(h => JSON.stringify((r as any)[h] ?? '')).join(',')));
                const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fileName; a.click(); URL.revokeObjectURL(url);
            } else {
                const blob = new Blob([JSON.stringify(filteredRows, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = fileName; a.click(); URL.revokeObjectURL(url);
            }
        } finally { setExporting(false); }
    }, [filteredRows, visibleColumns]);

    // Centralized Review & Save handler
    const handleReviewAndSave = useCallback(() => {
        if (!selected) return;
        const diffs = unsavedDiffs;
        modals.openConfirmModal({
            title: 'Confirm Save',
            children: (
                <Stack>
                    <Text size="sm">You are about to save {diffs.length} change(s). Review below:</Text>
                    {diffs.slice(0, 12).map((d, i) => (
                        <Text key={i} size="sm"><b>{d.path}:</b> {JSON.stringify(d.before)} → {JSON.stringify(d.after)}</Text>
                    ))}
                    {diffs.length > 12 && <Text size="xs" c="dimmed">+{diffs.length - 12} more…</Text>}
                </Stack>
            ),
            labels: { confirm: 'Save', cancel: 'Cancel' },
            onConfirm: async () => {
                await handleSave();
                try {
                    const entry = { ts: Date.now(), diffs, draft: JSON.parse(editJson || '{}') } as any;
                    const key = `workflowHistory:${safeTenant}:${selected.key}`;
                    const list = [entry, ...changeHistory].slice(0, 50);
                    localStorage.setItem(key, JSON.stringify(list));
                    setChangeHistory(list);
                    // ✅ REPOSITORY PATTERN: upsertWorkflowHistory via repository (was: upsertHistoryMutation)
                    try {
                        await repository.upsertWorkflowHistory(safeTenant, selected.key, entry);
                    } catch {}
                    baseDraftRef.current = entry.draft;
                    setUnsavedDiffs([]);
                } catch {}
            }
        });
    }, [selected, unsavedDiffs, editJson, changeHistory, safeTenant, repository, handleSave]);

    // Compute unsaved diffs whenever JSON changes
    useEffect(() => {
        try {
            if (!baseDraftRef.current) return;
            const current = editJson && editJson.trim().length ? JSON.parse(editJson) : {};
            setUnsavedDiffs(computeDiff(baseDraftRef.current as any, current));
        } catch {
            setUnsavedDiffs([]);
        }
    }, [editJson]);

    // Simple diff generator (shallow-to-moderate depth)
    function computeDiff(a: any, b: any, path: string = ''): Array<{ path: string; before: any; after: any }> {
        const diffs: Array<{ path: string; before: any; after: any }> = [];
        const keys = new Set([...(Object.keys(a || {})), ...(Object.keys(b || {}))]);
        for (const k of keys) {
            const p = path ? `${path}.${k}` : k;
            const av = (a || {})[k];
            const bv = (b || {})[k];
            const bothObjects = av && bv && typeof av === 'object' && typeof bv === 'object';
            if (bothObjects && !Array.isArray(av) && !Array.isArray(bv)) {
                diffs.push(...computeDiff(av, bv, p));
            } else if (JSON.stringify(av) !== JSON.stringify(bv)) {
                diffs.push({ path: p, before: av, after: bv });
            }
        }
        return diffs;
    }

    return (
        <Box>
            <Stack>
                <Group justify="space-between">
                    <Group>
                        <Title order={3}>{activeTab === 'table' ? 'Workflow Components Table' : 'Workflow Lifecycle Swimlanes'}</Title>
                        <Badge variant="light">Tenant: {safeTenant}</Badge>
                    </Group>
                    <Group>
                        <Button leftSection={<IconRefresh size={16} />} variant="light" loading={loadingComponents} onClick={() => loadData()}>
                            Refresh
                        </Button>
                    </Group>
                </Group>

                {/* Row Management moved into toolbar below */}

                <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                        💡 Click rows to select/deselect them. Use Ctrl/Cmd+click for multiple selection.
                    </Text>
                    <Group></Group>
                </Group>

                <Tabs value={activeTab} onChange={(value: string | null) => setActiveTab(value ?? 'table')}>
                    <Tabs.List>
                        {tabsMode !== 'table-only' && (
                        <Tabs.Tab value="overview">Workflow System Overview</Tabs.Tab>
                        )}
                        <Tabs.Tab value="table">Workflow Components Table</Tabs.Tab>
                        <Tabs.Tab value="drafts">My Drafts</Tabs.Tab>
                        <Tabs.Tab value="saved">My Saved</Tabs.Tab>
                        <Tabs.Tab value="history">Version History</Tabs.Tab>
                    </Tabs.List>

                    {tabsMode !== 'table-only' && (
                    <Tabs.Panel value="overview" pt="sm">
                        <Card withBorder style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                            <Group justify="space-between" mb="md">
                                <Title order={4}>Lifecycle Overview Dashboard</Title>
                                <Button
                                    variant="subtle"
                                    size="sm"
                                    leftSection={lifecycleDashboardOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                    onClick={() => setLifecycleDashboardOpen((v) => { const nv = !v; try { window.dispatchEvent(new CustomEvent('layout:contentToggled', { detail: { section: 'lifecycleDashboard', open: nv } })); } catch {} return nv; })}
                                >
                                    {lifecycleDashboardOpen ? 'Collapse' : 'Expand'}
                                </Button>
                            </Group>
                            <Collapse in={lifecycleDashboardOpen}>
                                <WorkflowLifecyclePlane viewMode="dashboard" />
                            </Collapse>
                        </Card>
                    </Tabs.Panel>
                    )}

                    <Tabs.Panel value="table" pt="sm">
                        {/* Full-bleed container for search + table to maximize width */}
                        <Box style={{ marginLeft: sidebarCollapsed ? 'calc(50% - 50vw)' : 0, marginRight: sidebarCollapsed ? 'calc(50% - 50vw)' : 0 }}>
                        {/* Workflow Lifecycle Search (advanced) */}
                        {hideSearch && (
                            <Group mb="sm" gap="xs">
                                <Button variant="light" onClick={handleSaveSearchDraft}>Save Draft</Button>
                                <Button onClick={handleSaveSearchFinal}>Save</Button>
                            </Group>
                        )}
                        {(!hideSearch) && (
                        <Card withBorder>
                            <WorkflowComponentsSearch
                                title={titleSearch}
                                allKinds={ALL_KINDS}
                                selectedKinds={selectedTypes}
                                onKindsChange={(k) => setSelectedTypes(k && k.length ? k : ['Instance'])}
                                viewMode={viewMode}
                                onViewModeChange={(m) => setViewMode(m)}
                                propertyList={searchProperties}
                                selectedProperty={searchProperty}
                                onPropertyChange={(p) => setSearchProperty(p)}
                                fullText={searchText}
                                onFullTextChange={(t: string) => setSearchText(t)}
                                dateRange={dateRange}
                                onDateRangeChange={(r) => setDateRange(r)}
                                columnsByCategory={columnsByCategory as any}
                                onToggleColumn={(f) => toggleColumnVisibility(f)}
                                valueSuggestions={valueSuggestions}
                                actorSuggestions={[]}
                                onApply={(cfg) => {
                                    setSelectedTypes(cfg.kinds && cfg.kinds.length ? cfg.kinds : ['Instance']);
                                    setViewMode(cfg.viewMode);
                                    setDateRange(cfg.dateRange);
                                    setSearchProperty(cfg.property || (cfg.fullText ? '*' : 'name'));
                                    setSearchText(cfg.fullText || '');
                                    setAdvancedClauses(cfg.clauses || []);
                                    setForceHydrateAll(!!cfg.hydrateAll || (cfg.property === '*'));
                                    setLiveUpdates(!!cfg.live);
                                    // Backend-driven audit filter (multi-select): add clauses for each actor
                                    const changedByList = Array.isArray(cfg.changedBy) ? cfg.changedBy : [];
                                    if (changedByList.length) {
                                        setAdvancedClauses(prev => ([
                                            ...prev,
                                            ...changedByList.map((u): AdvancedFilterClause => ({ field: 'updatedBy', operator: 'contains', value: String(u) }))
                                        ]));
                                    }
                                    applyFilters();
                                }}
                                onClear={() => { clearFilters(); setAdvancedClauses([]); setForceHydrateAll(false); }}
                                collapsible={true}
                            />
                            <Group mt="sm" gap="xs">
                                <Button variant="light" onClick={handleSaveSearchDraft}>Save Draft</Button>
                                <Button onClick={handleSaveSearchFinal}>Save</Button>
                            </Group>
                </Card>
                        )}

                        {/* Old separate search card removed; merged above */}

                        <Card withBorder>
                    <Group justify="space-between" mb="sm">
                        <Title order={4}>{titleTable}</Title>
                        <Group>
                            <Button variant="light" onClick={() => loadData()}>Refresh</Button>
                            {!isFullScreen ? (
                                <Button variant="light" onClick={() => { try { window.dispatchEvent(new Event('layout:collapseSidebar' as any)); window.dispatchEvent(new CustomEvent('layout:contentToggled', { detail: { section: 'lifecycleDashboard', open: false } })); } catch {} setSearchOpen(true); setLifecycleDashboardOpen(false); setIsFullScreen(true); }}>Full Screen</Button>
                            ) : (
                                <Button variant="light" onClick={() => { try { window.dispatchEvent(new Event('layout:expandSidebar' as any)); setIsFullScreen(false); } catch {} }}>Exit Full Screen</Button>
                            )}
                        </Group>
                    </Group>
                    <Paper withBorder p="sm" style={{ backgroundColor: '#fff' }}>
                    {(loadingComponents || hydratingDrafts) && (
                        <Center h={200}><Text>Loading workflows…</Text></Center>
                    )}
                    {!!errorMessage && (
                        <Center h={200}><Text c="red">{errorMessage}</Text></Center>
                    )}
                    {!loadingComponents && !errorMessage && (
                        <>
                            <Text size="sm" mb="xs">
                                Showing {filteredRows.length} workflows | View: {viewMode} | Columns: {visibleColumns.length}
                            </Text>
                            
                            {isFullScreen && (
                                <div ref={fullscreenToolbarRef} style={{ position: 'fixed', top: 70, left: 0, right: 0, zIndex: 1100, background: '#fff', borderBottom: '1px solid #e9ecef', padding: 8, minHeight: 64, pointerEvents: 'auto' }}>
                                    <Group wrap="wrap" gap="sm" justify="space-between" ta="center">
                                        <Group gap="sm">
                                            <Title order={4}>Workflow Lifecycle Swimlanes — Workflow Components Table</Title>
                                            <Badge variant="light">Tenant: {safeTenant}</Badge>
                                        </Group>
                                        <Group gap="xs">
                                            <Button size="xs" variant="filled" onClick={() => { try { window.dispatchEvent(new Event('layout:expandSidebar' as any)); } catch {} setIsFullScreen(false); }}>Exit Full Screen (Esc)</Button>
                                        </Group>
                                    </Group>
                                    <Group wrap="wrap" gap="xs" justify="space-between" ta="end" mt={6}>
                                        <Group wrap="wrap" gap="xs">
                                            <Select
                                                label="View"
                                                data={[{ value: 'compact', label: 'Compact' }, { value: 'standard', label: 'Standard' }, { value: 'detailed', label: 'Detailed' }]}
                                                value={viewMode}
                                                onChange={(value: string | null) => setViewMode((value ?? 'standard') as 'compact' | 'standard' | 'detailed')}
                                                w={120}
                                            />
                                            <Select
                                                label="Property"
                                                data={searchProperties}
                                                value={searchProperty}
                                                onChange={(v: string | null) => setSearchProperty(v ?? 'name')}
                                                w={160}
                                            />
                                            <TextInput label="Value" value={searchText} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.currentTarget.value)} w={220} />
                                            <DatePickerInput type="range" label="Date" value={dateRange} onChange={(v) => setDateRange(v as [Date | null, Date | null])} />
                                            <Menu shadow="md" width={260} withinPortal>
                                                <Menu.Target>
                                                    <Button variant="light">Columns</Button>
                                                </Menu.Target>
                                                <Menu.Dropdown>
                                                    <ScrollArea h={280}>
                                                        {Object.values(columnsByCategory).flat().map((col: { field?: string; title?: string }) => (
                                                            <Menu.Item key={col.field ?? ''} onClick={() => toggleColumnVisibility(col.field ?? '')}>
                                                                {col.title ?? col.field ?? ''}
                                                            </Menu.Item>
                                                        ))}
                                                    </ScrollArea>
                                                </Menu.Dropdown>
                                            </Menu>
                                            <Button leftSection={<IconSearch size={16} />} onClick={applyFilters}>Search</Button>
                                            <Button variant="light" onClick={clearFilters}>Clear</Button>
                                            <Button leftSection={<IconPlus size={14} />} variant="light" onClick={addNewRow}>Add Row</Button>
                                            <Menu withinPortal>
                                                <Menu.Target>
                                                    <Button variant="light">Export</Button>
                                                </Menu.Target>
                                                <Menu.Dropdown>
                                                    <Menu.Item onClick={() => exportRows('csv')}>CSV</Menu.Item>
                                                    <Menu.Item onClick={() => exportRows('json')}>JSON</Menu.Item>
                                                </Menu.Dropdown>
                                            </Menu>
                                            <Menu withinPortal>
                                                <Menu.Target>
                                                    <Button variant="light">History</Button>
                                                </Menu.Target>
                                                <Menu.Dropdown>
                                                    {changeHistory.length === 0 && <Menu.Item disabled>No history</Menu.Item>}
                                                    {changeHistory.slice(0, 10).map(h => (
                                                        <Menu.Item key={h.ts}>{new Date(h.ts).toLocaleString()} • {h.diffs.length} change(s)</Menu.Item>
                                                    ))}
                                                    <Menu.Divider />
                                                    <Menu.Item color="red" onClick={() => { try { if (selected) localStorage.removeItem(`workflowHistory:${safeTenant}:${selected.key}`); } catch {} setChangeHistory([]); }}>Clear History</Menu.Item>
                                                </Menu.Dropdown>
                                            </Menu>
                                            <Button variant="filled" onClick={handleReviewAndSave}>Review & Save</Button>
                                        </Group>
                                    </Group>
                                </div>
                            )}

                            <div ref={tableContainerRef} style={{ width: '100%', overflow: isFullScreen ? 'auto' : 'hidden', position: isFullScreen ? 'fixed' : 'relative', top: isFullScreen ? fullscreenTop : undefined, left: isFullScreen ? 0 : undefined, right: isFullScreen ? 0 : undefined, bottom: isFullScreen ? 0 : undefined, zIndex: isFullScreen ? 1000 : undefined, background: isFullScreen ? '#fff' : undefined, padding: isFullScreen ? 12 : 0, pointerEvents: 'auto' }}>
                                <div 
                                    ref={tableRef} 
                                    style={{ 
                                        height: isFullScreen ? `calc(100vh - ${fullscreenTop + 12}px)` : '400px',
                                        width: '100%',
                                        overflow: 'visible',
                                        border: '1px solid #e9ecef',
                                        borderRadius: '4px',
                                        backgroundColor: '#fff',
                                        position: 'relative'
                                    }} 
                                />
                            </div>
                            
                            {/* Debug fallback removed */}
                        </>
                    )}
                    </Paper>
                </Card>
                </Box>
                    </Tabs.Panel>
                    <Tabs.Panel value="drafts" pt="sm">
                        <WorkflowDraftsListView tenant={safeTenant} />
                    </Tabs.Panel>
                    <Tabs.Panel value="saved" pt="sm">
                        <WorkflowSavedListView tenant={safeTenant} />
                    </Tabs.Panel>
                    <Tabs.Panel value="history" pt="sm">
                        <WorkflowHistoryListView tenant={safeTenant} />
                    </Tabs.Panel>
                </Tabs>

                {selected && (
                    <Card withBorder>
                        <Group justify="space-between" mb="sm">
                            <Title order={4}>Workflow Details</Title>
                            <Group>
                                <Button
                                    variant={isEditing ? 'filled' : 'light'}
                                    leftSection={<IconEdit size={16} />}
                                    onClick={() => setIsEditing((v) => !v)}
                                >
                                    {isEditing ? 'Editing' : 'Edit'}
                                </Button>
                                <Button
                                    color="red"
                                    variant="outline"
                                    leftSection={<IconTrash size={16} />}
                                    onClick={handleDelete}
                                >
                                    Delete
                                </Button>
                                {isEditing && (
                                    <Button leftSection={<IconDeviceFloppy size={16} />} loading={saving} onClick={handleSave}>
                                        Save
                                    </Button>
                                )}
                            </Group>
                        </Group>
                        <SimpleGrid cols={{ base: 2 }} spacing="md">
                            <TextInput label="Key" value={selected.key} readOnly />
                            <TextInput label="Owner" value={selected.owner || ''} readOnly={!isEditing} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                try {
                                    const d = JSON.parse(editJson || '{}');
                                    d.owner = e.currentTarget.value;
                                    setEditJson(JSON.stringify(d, null, 2));
                                } catch {}
                            }} />
                            <TextInput label="Status" value={selected.status || ''} readOnly />
                            <TextInput label="Priority" value={selected.priority || ''} readOnly={!isEditing} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                try {
                                    const d = JSON.parse(editJson || '{}');
                                    d.priority = e.currentTarget.value;
                                    setEditJson(JSON.stringify(d, null, 2));
                                } catch {}
                            }} />
                        </SimpleGrid>
                        <Box mt="md">
                            <JsonInput
                                label="Draft (JSON)"
                                value={editJson}
                                onChange={setEditJson}
                                autosize
                                minRows={8}
                                maxRows={18}
                                formatOnBlur
                                validationError="Invalid JSON"
                                readOnly={!isEditing}
                            />
                        </Box>
                        <Group mt="md" justify="space-between">
                            <Group>
                                <Button leftSection={<IconArrowBackUp size={16} />} variant="light" onClick={() => { try { const base = baseDraftRef.current || {}; setEditJson(JSON.stringify(base, null, 2)); notifications.show({ title: 'Reverted', message: 'Reverted to last loaded version', color: 'gray' }); } catch {} }}>Revert</Button>
                                <Button leftSection={<IconHistory size={16} />} variant="light" onClick={() => modals.open({ title: 'Pending Changes', children: (
                                    <Stack>
                                        {unsavedDiffs.length === 0 ? <Text size="sm">No changes.</Text> : unsavedDiffs.slice(0, 12).map((d, i) => (
                                            <Text key={i} size="sm"><b>{d.path}:</b> {JSON.stringify(d.before)} → {JSON.stringify(d.after)}</Text>
                                        ))}
                                        {unsavedDiffs.length > 12 && <Text size="xs" c="dimmed">+{unsavedDiffs.length - 12} more…</Text>}
                                    </Stack>
                                ) })}>Preview Changes</Button>
                            </Group>
                            <Group>
                                <Button leftSection={<IconDeviceFloppy size={16} />} onClick={handleReviewAndSave}>Review & Save</Button>
                            </Group>
                        </Group>

                        <Box mt="md">
                            <RelationshipInspector tenant={safeTenant} selected={selected as any} rows={rows as any} onHydrateAll={hydrateAllDraftsIfNeeded} />
                        </Box>
                    </Card>
                )}

                {/* NEW: DrillDownModal with 4-tab entity explorer */}
                {selected && (
                    <DrillDownModal
                        opened={relOpen}
                        onClose={() => setRelOpen(false)}
                        entityId={selected.id}
                        entityType={selected.type || 'WorkflowDefinition'}
                        entityData={{
                            name: selected.name,
                            status: selected.status,
                            createdAt: selected.createdAt || Date.now(),
                            updatedAt: selected.updatedAt || Date.now(),
                            createdBy: selected.owner || 'unknown',
                            metadata: selected,
                        }}
                    />
                )}

                {/* LEGACY: Keep RelationshipModal for backward compatibility (hidden by default) */}
                <RelationshipModal tenant={safeTenant} opened={false} onClose={() => setRelOpen(false)} selected={selected as any} rows={rows as any} onHydrateAll={hydrateAllDraftsIfNeeded} />
            </Stack>
        </Box>
    );
};

export default TabulatorLifecycleDashboard;


